/**
 * Cart Drawer — Hyper-style
 */
(function () {
  'use strict';

  if (window.__cartDrawerInitialized) return;
  window.__cartDrawerInitialized = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
  } else {
    initCartDrawer();
  }

  const MILESTONE_CHECK_SVG =
    '<svg class="cart-drawer__milestone-check" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const configEl = document.querySelector('[data-cart-drawer-config]');
    let config = { openOnAdd: true, sectionId: drawer.dataset.sectionId };
    if (configEl) {
      try {
        config = { ...config, ...JSON.parse(configEl.textContent) };
      } catch (e) {
        /* ignore */
      }
    }

    const openOnAdd = drawer.dataset.openOnAdd !== 'false' && config.openOnAdd !== false;
    const sectionId = config.sectionId || drawer.dataset.sectionId;

    let notesSaveTimeout = null;
    const removingKeys = new Set();
    let sectionRenderToken = 0;

    function setDrawerUpdating(isUpdating) {
      drawer.classList.toggle('is-updating', isUpdating);
      const content = getContent();
      if (content) content.setAttribute('aria-busy', isUpdating ? 'true' : 'false');
    }

    async function refreshDrawerFromCart() {
      const token = ++sectionRenderToken;
      setDrawerUpdating(true);
      try {
        await renderSection();
      } finally {
        if (token === sectionRenderToken) setDrawerUpdating(false);
      }
    }

    function getContent() {
      return drawer.querySelector('.cart-drawer__content');
    }

    function hasGsap() {
      return typeof gsap !== 'undefined';
    }

    function clearDrawerShellGsap() {
      const content = getContent();
      const overlay = drawer.querySelector('[data-cart-overlay]');
      if (hasGsap()) {
        if (content) {
          gsap.killTweensOf(content);
          gsap.set(content, { clearProps: 'transform' });
        }
        if (overlay) {
          gsap.killTweensOf(overlay);
          gsap.set(overlay, { clearProps: 'opacity' });
        }
      } else {
        if (content) content.style.transform = '';
        if (overlay) overlay.style.opacity = '';
      }
    }

    function initGsapLayout() {
      // Sheets are positioned/animated by ui-sheet (CSS); nothing to preset here.
    }

    function refreshRefs() {
      return {
        closeBtn: drawer.querySelector('[data-cart-close]'),
        overlay: drawer.querySelector('[data-cart-overlay]'),
        filled: drawer.querySelector('[data-cart-drawer-filled]'),
        emptyWrap: drawer.querySelector('[data-cart-drawer-empty-wrap]'),
        itemsContainer: drawer.querySelector('[data-cart-drawer-items]'),
        countEl: drawer.querySelector('[data-cart-drawer-count]'),
        footer: drawer.querySelector('[data-cart-drawer-footer]'),
        footerEmpty: drawer.querySelector('[data-cart-footer-empty]'),
        footerFilled: drawer.querySelector('[data-cart-footer-filled]'),
        pills: drawer.querySelector('[data-cart-drawer-pills]'),
        milestones: drawer.querySelector('[data-cart-drawer-milestones]'),
        notesTextarea: drawer.querySelector('[data-cart-note]'),
      };
    }

    function openDrawer() {
      if (drawer.getAttribute('aria-hidden') === 'false') return;

      closeActiveSheet(true);
      clearDrawerShellGsap();

      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'true');
      });

      requestAnimationFrame(() => {
        animateDrawerOpen();
        if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
      });

      window.dispatchEvent(new CustomEvent('cart:opened'));
      loadRecommendations();
    }

    function closeDrawer() {
      if (drawer.getAttribute('aria-hidden') === 'true') return;

      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });

      const content = getContent();
      const onClosed = () => {
        clearDrawerShellGsap();
        closeActiveSheet(true);
        if (hasGsap()) initGsapLayout();
        window.dispatchEvent(new CustomEvent('cart:closed'));
      };

      if (content) {
        content.addEventListener('transitionend', (e) => {
          if (e.propertyName === 'transform') onClosed();
        }, { once: true });
      } else {
        onClosed();
      }
    }

    function toggleDrawer() {
      if (drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
      else openDrawer();
    }

    function animateDrawerOpen() {
      const refs = refreshRefs();
      if (!hasGsap()) return;

      if (refs.milestones) {
        const fill = refs.milestones.querySelector('[data-milestones-fill]');
        if (fill) {
          const target = fill.style.width || window.getComputedStyle(fill).width || '0%';
          fill.style.width = '0%';
          gsap.to(fill, { width: target, duration: 1, ease: 'power2.out', delay: 0.2 });
        }
      }

      if (refs.filled && !refs.filled.hidden) {
        const items = refs.filled.querySelectorAll('[data-cart-drawer-item]');
        const upsells = refs.filled.querySelectorAll('.cart-upsell-card');
        if (items.length) {
          gsap.fromTo(
            items,
            { opacity: 0, y: 12 },
            { opacity: 1, y: 0, duration: 0.4, stagger: 0.06, ease: 'power3.out', delay: 0.15 }
          );
        }
        if (upsells.length) {
          gsap.fromTo(
            upsells,
            { opacity: 0, x: 16 },
            { opacity: 1, x: 0, duration: 0.35, stagger: 0.05, ease: 'power2.out', delay: 0.25 }
          );
        }
      } else if (refs.emptyWrap && !refs.emptyWrap.hidden) {
        const icon = drawer.querySelector('[data-empty-cart-icon]');
        const navItems = drawer.querySelectorAll('[data-empty-nav-item]');
        const perks = drawer.querySelectorAll('[data-empty-perk-item]');
        if (icon) {
          gsap.fromTo(
            icon,
            { scale: 0.85, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.5)' }
          );
        }
        if (navItems.length) {
          gsap.fromTo(
            navItems,
            { opacity: 0, x: -10 },
            { opacity: 1, x: 0, duration: 0.3, stagger: 0.05, ease: 'power2.out', delay: 0.1 }
          );
        }
        if (perks.length) {
          gsap.fromTo(
            perks,
            { opacity: 0, y: 10 },
            { opacity: 1, y: 0, duration: 0.35, stagger: 0.08, ease: 'power2.out', delay: 0.15 }
          );
        }
      }
    }

    // Sheets (note / shipping / coupon / upsell) are driven by the shared
    // ui-sheet component (ui-sheet.js). closeActiveSheet lets the drawer
    // open/close reset also dismiss any sheet layered over it.
    function closeActiveSheet() {
      if (window.UISheet) window.UISheet.close();
    }

    function getCartRoot() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    function discountWasApplied(cart) {
      return (
        (cart.cart_level_discount_applications || []).length > 0 || Number(cart.total_discount || 0) > 0
      );
    }

    function setCouponLoading(isLoading) {
      drawer.querySelectorAll('[data-coupon-apply], [data-coupon-apply-code]').forEach((btn) => {
        btn.disabled = isLoading;
        if (isLoading) btn.setAttribute('aria-busy', 'true');
        else btn.removeAttribute('aria-busy');
      });
    }

    function collectDiscountErrorText(data) {
      const chunks = [];
      if (data?.description) chunks.push(String(data.description));
      if (data?.message && data.message !== 'Cart Error') chunks.push(String(data.message));
      if (Array.isArray(data?.errors)) {
        data.errors.forEach((err) => chunks.push(String(err)));
      } else if (data?.errors && typeof data.errors === 'object') {
        Object.values(data.errors).forEach((val) => {
          if (Array.isArray(val)) val.forEach((item) => chunks.push(String(item)));
          else chunks.push(String(val));
        });
      }
      return chunks.join(' ').toLowerCase();
    }

    function parseDiscountError(res, data, code) {
      const text = collectDiscountErrorText(data);

      if (text.includes('expir')) {
        return 'This discount code has expired.';
      }
      if (
        text.includes('not valid') ||
        text.includes('invalid') ||
        text.includes("isn't valid") ||
        text.includes('is not valid') ||
        text.includes('does not exist') ||
        text.includes("doesn't exist") ||
        text.includes('not found') ||
        text.includes('unable to find') ||
        text.includes('no valid discount') ||
        text.includes('unknown discount')
      ) {
        return 'This discount code is invalid or does not exist.';
      }
      if (text.includes('already been applied') || text.includes('already applied')) {
        return 'This discount code is already applied to your cart.';
      }
      if (
        text.includes('not applicable') ||
        text.includes("doesn't apply") ||
        text.includes('does not apply') ||
        text.includes('requirements') ||
        text.includes('minimum') ||
        text.includes('eligible')
      ) {
        return (
          data?.description ||
          data?.message ||
          "This discount code doesn't apply to your cart."
        );
      }

      if (data?.description) return data.description;
      if (data?.message && data.message !== 'Cart Error') return data.message;

      if (res && !res.ok) {
        return `We couldn't apply "${code}". Check the code and try again.`;
      }

      return 'This discount code could not be applied. It may be expired, invalid, or not eligible for your cart.';
    }

    function showCouponError(message) {
      const errorEl = drawer.querySelector('[data-coupon-error]');
      const input = drawer.querySelector('[data-coupon-input]');
      const form = drawer.querySelector('.cart-drawer__coupon-form');
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
      if (form) form.classList.add('is-error');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
      }
    }

    function clearCouponError() {
      const errorEl = drawer.querySelector('[data-coupon-error]');
      const input = drawer.querySelector('[data-coupon-input]');
      const form = drawer.querySelector('.cart-drawer__coupon-form');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      if (form) form.classList.remove('is-error');
      if (input) input.removeAttribute('aria-invalid');
    }

    async function saveNote(note) {
      try {
        await fetch(`${getCartRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        });
      } catch (e) {
        console.error('Failed to save note:', e);
      }
    }

    async function applyDiscountCode(code) {
      const trimmed = (code || '').trim();
      clearCouponError();

      if (!trimmed) {
        showCouponError('Please enter a discount code.');
        return false;
      }

      if (window.CartAPI?.beginChange) window.CartAPI.beginChange({ action: 'discount' });
      else window.dispatchEvent(new CustomEvent('cart:changing', { detail: { action: 'discount' } }));

      setCouponLoading(true);

      try {
        const res = await fetch(`${getCartRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: trimmed }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const message = parseDiscountError(res, data, trimmed);
          showCouponError(message);
          window.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
          return false;
        }
        if (!discountWasApplied(data)) {
          const message = parseDiscountError(res, data, trimmed);
          showCouponError(message);
          window.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
          return false;
        }
        clearCouponError();
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
        closeActiveSheet();
        return true;
      } catch (e) {
        const message = e.message || 'Could not apply code. Please try again.';
        showCouponError(message);
        window.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
        return false;
      } finally {
        setCouponLoading(false);
        if (window.CartAPI?.endChange) window.CartAPI.endChange();
        else window.dispatchEvent(new CustomEvent('cart:idle'));
      }
    }

    function formatShippingPrice(price) {
      const cents = Math.round(parseFloat(price || 0) * 100);
      return formatMoney(cents);
    }

    async function calculateShippingRates() {
      const countryEl = drawer.querySelector('[data-shipping-country]');
      const zipEl = drawer.querySelector('[data-shipping-zip]');
      const resultEl = drawer.querySelector('[data-shipping-result]');
      const calcBtn = drawer.querySelector('[data-shipping-calculate]');
      if (!countryEl || !zipEl || !resultEl) return;

      const country = countryEl.value?.trim();
      const zip = zipEl.value?.trim();
      if (!country || !zip) {
        resultEl.hidden = false;
        resultEl.textContent = 'Please enter country and postal code.';
        return;
      }

      const params = new URLSearchParams({
        'shipping_address[country]': country,
        'shipping_address[zip]': zip,
      });

      const root = getCartRoot();
      const query = params.toString();

      if (calcBtn) {
        calcBtn.disabled = true;
        calcBtn.setAttribute('aria-busy', 'true');
      }
      resultEl.hidden = false;
      resultEl.textContent = 'Calculating…';

      try {
        const prepareRes = await fetch(`${root}cart/prepare_shipping_rates.json?${query}`, { method: 'POST' });
        if (!prepareRes.ok) throw new Error('Could not start shipping calculation');

        let rates = null;
        for (let attempt = 0; attempt < 10; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 500));
          const res = await fetch(`${root}cart/async_shipping_rates.json?${query}`);
          if (!res.ok) continue;
          const data = await res.json();
          if (data?.shipping_rates != null) {
            rates = data.shipping_rates;
            break;
          }
        }

        if (!rates || rates.length === 0) {
          resultEl.textContent = 'No shipping rates found for this address.';
          return;
        }

        resultEl.innerHTML = rates
          .map((rate) => {
            const name = escapeHtml(rate.presentment_name || rate.name || 'Shipping');
            const price = formatShippingPrice(rate.price);
            return `<div>${name}: ${price}</div>`;
          })
          .join('');
      } catch (e) {
        console.error('Shipping rates failed:', e);
        resultEl.textContent = 'Unable to calculate shipping. Please try again.';
      } finally {
        if (calcBtn) {
          calcBtn.disabled = false;
          calcBtn.removeAttribute('aria-busy');
        }
      }
    }

    async function updateQuantity(key, newQuantity) {
      if (window.CartAPI) {
        await window.CartAPI.updateItem(key, newQuantity);
        return;
      }

      window.dispatchEvent(new CustomEvent('cart:changing', { detail: { action: 'updateItem', key } }));
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQuantity }),
        });
        if (!response.ok) throw new Error('Failed to update cart');
        const cart = await response.json();
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } finally {
        window.dispatchEvent(new CustomEvent('cart:idle'));
      }
    }

    async function removeItem(key) {
      if (drawer.classList.contains('is-updating')) return;
      if (removingKeys.has(key)) return;
      const itemEl = drawer.querySelector(`[data-line-key="${key}"]`);
      if (!itemEl || itemEl.classList.contains('is-removing')) return;

      removingKeys.add(key);
      itemEl.classList.add('is-removing');

      try {
        if (window.CartAPI) await window.CartAPI.removeItem(key);
        else await updateQuantity(key, 0);
      } catch (e) {
        console.error('Failed to remove item:', e);
        itemEl.classList.remove('is-removing');
      } finally {
        setTimeout(() => removingKeys.delete(key), 500);
      }
    }

    async function renderSection() {
      if (!sectionId) return refreshCartFallback();

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}?sections=${encodeURIComponent(sectionId)}`;
        const res = await fetch(url);
        const json = await res.json();
        const html = json[sectionId];
        if (!html) return refreshCartFallback();

        const tpl = document.createElement('div');
        tpl.innerHTML = html;
        const newDrawer = tpl.querySelector('[data-cart-drawer]');
        if (!newDrawer) return refreshCartFallback();

        const wasOpen = drawer.getAttribute('aria-hidden') === 'false';
        const newContent = newDrawer.querySelector('.cart-drawer__content');
        const content = getContent();
        if (newContent && content) {
          content.innerHTML = newContent.innerHTML;
        }

        drawer.dataset.openOnAdd = newDrawer.dataset.openOnAdd;
        attachItemListeners();
        bindSheetEvents();
        updateHeaderCountFromCart();
        loadRecommendations();

        if (wasOpen) {
          requestAnimationFrame(animateDrawerOpen);
        }

        if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
      } catch (e) {
        console.error('Section render failed:', e);
        return refreshCartFallback();
      }
    }

    /* ---- Dynamic upsells: Shopify product recommendations, rendered as PCH
       cards by the cart-recommendations section and injected into the drawer ---- */
    function bindUpsellAddButtons() {
      drawer.querySelectorAll('[data-cart-upsell-add]').forEach((btn) => {
        btn.onclick = (e) => {
          e.preventDefault();
          if (drawer.classList.contains('is-updating')) return;
          quickAddUpsell(btn);
        };
      });
    }

    let recoToken = 0;
    async function loadRecommendations() {
      const mount = drawer.querySelector('[data-cart-recos]');
      if (!mount) return;
      const wrap = mount.closest('[data-cart-drawer-upsells]');
      const token = ++recoToken;
      try {
        const cart = await (await fetch(`${getCartRoot()}cart.js`)).json();
        if (token !== recoToken) return;
        if (!cart || !cart.items || cart.item_count === 0) {
          if (wrap) wrap.hidden = true;
          return;
        }
        // Anchor recommendations on the highest-value item in the cart.
        let base = cart.items[0];
        cart.items.forEach((it) => {
          if ((it.final_line_price || it.line_price || 0) > (base.final_line_price || base.line_price || 0)) base = it;
        });
        const cartProductIds = new Set(cart.items.map((it) => String(it.product_id)));
        const limit = parseInt(mount.getAttribute('data-reco-limit'), 10) || 6;
        const intent = mount.getAttribute('data-reco-intent') || 'related';
        const url = `${getCartRoot()}recommendations/products?section_id=cart-recommendations`
          + `&product_id=${base.product_id}&intent=${encodeURIComponent(intent)}&limit=${limit}`;
        const html = await (await fetch(url)).text();
        if (token !== recoToken) return;
        const tpl = document.createElement('div');
        tpl.innerHTML = html;
        const cards = Array.from(tpl.querySelectorAll('[data-cart-upsell-card]'))
          .filter((c) => !cartProductIds.has(String(c.getAttribute('data-product-id'))))
          .slice(0, limit);
        mount.querySelectorAll('[data-cart-upsell-card]').forEach((c) => c.remove());
        const spacer = mount.querySelector('.cart-drawer__upsells-spacer');
        cards.forEach((card) => { spacer ? mount.insertBefore(card, spacer) : mount.appendChild(card); });
        if (cards.length) {
          if (wrap) wrap.hidden = false;
          bindUpsellAddButtons();
          if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
        } else if (wrap) {
          wrap.hidden = true;
        }
      } catch (e) {
        if (wrap) wrap.hidden = true;
      }
    }

    async function refreshCartFallback() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        updateHeaderCount(cart.item_count);
        setCartState(cart.item_count === 0);
        if (cart.item_count > 0 && refreshRefs().itemsContainer) {
          renderCartItems(cart.items);
        }
        updateMilestones(cart.total_price);
        updateFooterTotals(cart);
      } catch (e) {
        console.error('Failed to refresh cart:', e);
      }
    }

    async function updateHeaderCountFromCart() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        updateHeaderCount(cart.item_count);
      } catch (e) {
        /* ignore */
      }
    }

    function updateHeaderCount(count) {
      const refs = refreshRefs();
      if (refs.countEl) refs.countEl.textContent = count;
      const headerCartCount = document.querySelector('[data-cart-count]');
      if (headerCartCount) {
        headerCartCount.textContent = count;
        headerCartCount.classList.toggle('is-visible', count > 0);
      }
    }

    function setCartState(isEmpty) {
      const refs = refreshRefs();
      if (refs.filled) refs.filled.hidden = isEmpty;
      if (refs.emptyWrap) refs.emptyWrap.hidden = !isEmpty;
      if (refs.footerEmpty) refs.footerEmpty.hidden = !isEmpty;
      if (refs.footerFilled) refs.footerFilled.hidden = isEmpty;
      if (refs.pills) refs.pills.hidden = isEmpty;
    }

    function updateMilestones(cartTotalCents) {
      const el = drawer.querySelector('[data-cart-drawer-milestones]');
      if (!el) return;

      const t1 = parseInt(el.dataset.threshold1, 10) || 0;
      const t2 = parseInt(el.dataset.threshold2, 10) || 0;
      const t3 = parseInt(el.dataset.threshold3, 10) || 0;
      const max = t3 || 1;
      const pct = Math.min(100, (cartTotalCents / max) * 100);

      const fill = el.querySelector('[data-milestones-fill]');
      if (fill) fill.style.width = `${pct}%`;

      const unlocked = [
        cartTotalCents >= t1 && t1 > 0,
        cartTotalCents >= t2 && t2 > 0,
        cartTotalCents >= t3 && t3 > 0,
      ];

      el.querySelectorAll('[data-milestone]').forEach((node, i) => {
        const isComplete = unlocked[i];
        node.classList.toggle('is-complete', isComplete);
        node.classList.toggle('is-next', i === 2 && unlocked[1] && !isComplete);
        const dot = node.querySelector('.cart-drawer__milestone-dot');
        if (dot) {
          const hasCheck = dot.querySelector('.cart-drawer__milestone-check');
          if (isComplete && !hasCheck) {
            dot.innerHTML = MILESTONE_CHECK_SVG;
          } else if (!isComplete && hasCheck) {
            dot.innerHTML = '';
          }
        }
      });

      const msg = el.querySelector('[data-milestones-message]');
      if (!msg) return;

      const progressText = el.dataset.progressText || '';
      const successText = el.dataset.successText || '';
      const labels = [el.dataset.label1, el.dataset.label2, el.dataset.label3];

      if (unlocked[2] || (t3 > 0 && cartTotalCents >= t3)) {
        let goals = labels.filter((_, i) => unlocked[i]).join(' & ');
        msg.textContent = successText.replace('[goals]', goals);
        return;
      }

      let nextThreshold = t1;
      let nextLabel = labels[0];
      if (cartTotalCents < t1) {
        nextThreshold = t1;
        nextLabel = labels[0];
      } else if (cartTotalCents < t2) {
        nextThreshold = t2;
        nextLabel = labels[1];
      } else {
        nextThreshold = t3;
        nextLabel = labels[2];
      }

      const remaining = Math.max(0, nextThreshold - cartTotalCents);
      msg.textContent = progressText
        .replace('[amount]', formatMoney(remaining))
        .replace('[goal]', nextLabel);
    }

    function updateFooterTotals(cart) {
      const subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
      const discountWrap = drawer.querySelector('[data-cart-footer-discount]');
      const totalLabel = drawer.querySelector('[data-cart-total-label]');
      const applications = cart.cart_level_discount_applications || [];
      const hasDiscount = applications.length > 0 || cart.original_total_price > cart.total_price;

      if (discountWrap) {
        discountWrap.hidden = !hasDiscount;
        if (hasDiscount && applications.length > 0) {
          discountWrap.innerHTML = applications
            .map(
              (discount) => `
            <div class="cart-drawer__footer-discount-row">
              <span class="cart-drawer__footer-discount-label">
                <span class="ph-bold ph-tag"></span>
                ${escapeHtml(discount.title)}
              </span>
              <span class="cart-drawer__footer-discount-amount">-${formatMoney(discount.total_allocated_amount)}</span>
            </div>`
            )
            .join('');
          if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
        }
      }
      if (totalLabel) totalLabel.textContent = hasDiscount ? 'Subtotal' : 'Estimated total';

      if (subtotalEl) {
        if (cart.original_total_price > cart.total_price) {
          subtotalEl.innerHTML = `<span class="cart-drawer__subtotal-current">${formatMoney(cart.total_price)}</span><span class="cart-drawer__subtotal-compare">${formatMoney(cart.original_total_price)}</span>`;
        } else {
          subtotalEl.textContent = formatMoney(cart.total_price);
        }
      }
    }

    function buildDisplayTitle(item) {
      return item.product_title || item.title;
    }

    function buildVariantSubtitle(item) {
      if (!item.variant_title || item.variant_title === 'Default Title') return '';
      return item.variant_title;
    }

    function renderCartItems(items) {
      const container = drawer.querySelector('[data-cart-drawer-items]');
      if (!container) return;

      container.innerHTML = items
        .map((item, index) => {
          const title = escapeHtml(buildDisplayTitle(item));
          const variant = buildVariantSubtitle(item);
          const showSale =
            item.original_line_price !== item.final_line_price ||
            item.original_price > item.final_price;
          const hasCompare = showSale;
          let priceHtml = `<span class="product-card-horizontal__price">${formatMoney(item.final_line_price)}</span>`;
          if (hasCompare) {
            const compare =
              item.original_line_price !== item.final_line_price
                ? item.original_line_price
                : item.original_price * item.quantity;
            priceHtml = `<span class="product-card-horizontal__price product-card-horizontal__price--sale">${formatMoney(item.final_line_price)}</span><span class="product-card-horizontal__compare-price">${formatMoney(compare)}</span>`;
          }

          // Mirror the shared badge system (snippets/badge-list.liquid) markup so
          // JS-rebuilt cart lines match server-rendered ones.
          const badgeHtml = showSale
            ? '<div class="product-card-horizontal__badges"><span class="tag tag--mini tag--red tag--solid product-card__badge"><span class="tag__icon"><i class="ph-bold ph-tag"></i></span><span class="tag__text">Sale</span></span></div>'
            : '';

          const mediaHtml = item.image
            ? `<img src="${getSizedImageUrl(item.image, '240x')}" alt="${title}" class="product-card-horizontal__image">`
            : '<span class="product-card-horizontal__placeholder" aria-hidden="true"><span class="ph-bold ph-package"></span></span>';

          return `
        <article
          class="product-card-horizontal cart-drawer__line-item"
          style="--item-index: ${index};"
          data-product-card-horizontal
          data-cart-drawer-item
          data-line-key="${item.key}"
          data-variant-id="${item.variant_id}"
        >
          <a href="${item.url}" class="product-card-horizontal__media-link" aria-label="${title}">
            <span class="product-card-horizontal__media">${mediaHtml}</span>
          </a>
          <div class="product-card-horizontal__content">
            <div class="product-card-horizontal__top">
              ${badgeHtml}
              <h3 class="product-card-horizontal__title">${title}</h3>
              ${variant ? `<p class="product-card-horizontal__subtitle">${escapeHtml(variant)}</p>` : ''}
              <button type="button" class="cart-drawer__line-remove" data-remove-item data-line-key="${item.key}" aria-label="Remove">
                <span class="ph-bold ph-x"></span>
              </button>
            </div>
            <div class="product-card-horizontal__bottom">
              <div class="product-card-horizontal__price-row">${priceHtml}</div>
              <div class="cart-drawer__qty" data-cart-qty>
                <button type="button" class="cart-drawer__qty-btn" data-qty-decrease data-line-key="${item.key}"><span class="ph-bold ph-minus"></span></button>
                <span class="cart-drawer__qty-value" data-qty-value>${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-qty-increase data-line-key="${item.key}"><span class="ph-bold ph-plus"></span></button>
              </div>
            </div>
          </div>
        </article>`;
        })
        .join('');

      attachItemListeners();
    }

    function attachItemListeners() {
      drawer.querySelectorAll('[data-qty-decrease]').forEach((btn) => {
        btn.onclick = async function () {
          if (drawer.classList.contains('is-updating')) return;
          const key = this.dataset.lineKey;
          const valueEl = this.parentElement.querySelector('[data-qty-value]');
          const currentQty = parseInt(valueEl.textContent, 10) || 1;
          const newQty = Math.max(1, currentQty - 1);
          if (newQty === currentQty) return;
          try {
            await updateQuantity(key, newQty);
          } catch (e) {
            console.error('Failed to update quantity:', e);
          }
        };
      });

      drawer.querySelectorAll('[data-qty-increase]').forEach((btn) => {
        btn.onclick = async function () {
          if (drawer.classList.contains('is-updating')) return;
          const key = this.dataset.lineKey;
          const valueEl = this.parentElement.querySelector('[data-qty-value]');
          const newQty = (parseInt(valueEl.textContent, 10) || 1) + 1;
          try {
            await updateQuantity(key, newQty);
          } catch (e) {
            console.error('Failed to update quantity:', e);
          }
        };
      });

      drawer.querySelectorAll('[data-remove-item]').forEach((btn) => {
        btn.onclick = function () {
          if (drawer.classList.contains('is-updating')) return;
          removeItem(this.dataset.lineKey);
        };
      });
    }

    // ---- Upsell variant helpers (mirror shop-the-look's resolver) ----
    function upsellSelEsc(v) { return String(v).replace(/["\\]/g, '\\$&'); }
    function upsellEsc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
      );
    }
    function upsellDeriveOptions(prod) {
      return (prod.optionNames || []).map((name, idx) => {
        const seen = {}, values = [];
        (prod.variants || []).forEach((v) => {
          const val = v.options[idx];
          if (val != null && !seen[val]) { seen[val] = 1; values.push(val); }
        });
        return { name, values, idx };
      });
    }
    function upsellValueAvailable(prod, idx, value, chosen) {
      return prod.variants.some((v) => {
        if (!v.available) return false;
        if (String(v.options[idx]) !== String(value)) return false;
        for (let k = 0; k < chosen.length; k++) {
          if (k === idx) continue;
          if (chosen[k] != null && String(v.options[k]) !== String(chosen[k])) return false;
        }
        return true;
      });
    }
    function upsellResolveVariant(prod, chosen) {
      if (chosen.some((c) => c == null)) return null;
      let match = null;
      prod.variants.some((v) => {
        const ok = chosen.every((c, k) => String(v.options[k]) === String(c));
        if (ok) { match = v; return true; }
        return false;
      });
      return match;
    }
    function upsellIsColorOpt(name) { return /colou?r/i.test(name || ''); }
    function upsellSwatchHex(name) {
      try {
        if (window.UntamedColorSwatches && typeof window.UntamedColorSwatches.resolve === 'function') {
          const hex = window.UntamedColorSwatches.resolve(name);
          if (hex) return hex;
        }
      } catch (e) {}
      const n = String(name || '').trim().toLowerCase().replace(/\s+/g, '');
      return /^[a-z]+$/.test(n) ? n : '';
    }

    function openUpsellVariantSheet(prod) {
      const sheetEl = document.querySelector('[data-ui-sheet="cart-upsell"]');
      const body = sheetEl ? sheetEl.querySelector('[data-cart-upsell-sheet-body]') : null;
      if (!body || !window.UISheet) {
        if (prod.defaultVariantId && window.CartAPI) window.CartAPI.addItem(prod.defaultVariantId, 1);
        return;
      }
      const options = upsellDeriveOptions(prod);
      const chosen = options.map((o) => (o.values.length === 1 ? o.values[0] : null));
      const VP = (window.UntamedSheets && window.UntamedSheets.vp) || {};

      function render() {
        const ready = !!upsellResolveVariant(prod, chosen);
        body.innerHTML = window.UntamedSheets.renderVariantPicker([{
          pi: 0, title: prod.title, image: prod.image,
          options: options.map((o) => {
            const color = upsellIsColorOpt(o.name);
            return {
              name: o.name, oi: o.idx, isColor: color,
              selected: color ? chosen[o.idx] : null,
              values: o.values.map((val) => {
                return { value: val, selected: String(chosen[o.idx]) === String(val), available: upsellValueAvailable(prod, o.idx, val, chosen), hex: color ? upsellSwatchHex(val) : null };
              })
            };
          })
        }], {
          addAttr: 'data-cart-upsell-sheet-add',
          addLabel: VP.add || 'Add to bag',
          selectLabel: VP.select || 'Select options',
          ready: ready
        });
        bind();
      }
      function bind() {
        body.querySelectorAll('[data-vp-val]').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.getAttribute('aria-disabled') === 'true') return;
            const oi = parseInt(btn.getAttribute('data-vp-oi'), 10);
            const val = btn.getAttribute('data-vp-val');
            chosen[oi] = String(chosen[oi]) === String(val) ? null : val;
            render();
          });
        });
        const add = body.querySelector('[data-cart-upsell-sheet-add]');
        if (add) add.addEventListener('click', async () => {
          const v = upsellResolveVariant(prod, chosen);
          if (!v || !window.CartAPI) return;
          add.disabled = true;
          try {
            await window.CartAPI.addItem(v.id, 1);
            window.UISheet.close();
          } catch (e) {
            console.error(e);
            add.disabled = false;
          }
        });
      }

      render();
      window.UISheet.open('cart-upsell', { title: prod.title || 'Choose your options' });
    }

    function upsellAddedFeedback(btn) {
      if (!btn) return;
      if (hasGsap()) {
        gsap.fromTo(btn, { rotate: 0 }, { rotate: 360, duration: 0.5, ease: 'power2.out' });
      }
      btn.classList.add('is-added');
      const icon = btn.querySelector('.ph-bold');
      if (icon && window.UntamedIcons) window.UntamedIcons.setIcon(icon, 'check');
      else if (icon) icon.className = 'ph-bold ph-check';
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (icon && window.UntamedIcons) window.UntamedIcons.setIcon(icon, 'plus');
        else if (icon) icon.className = 'ph-bold ph-plus';
        if (hasGsap()) gsap.set(btn, { rotate: 0 });
      }, 1500);
    }

    // CartAPI-only add. Multi-variant products open the shared picker sheet.
    async function quickAddUpsell(btn) {
      const card = btn ? btn.closest('[data-cart-upsell-card]') : null;
      const variantCount = card ? parseInt(card.getAttribute('data-variant-count'), 10) || 1 : 1;

      if (card && variantCount > 1) {
        const dataEl = card.querySelector('[data-cart-upsell-variants]');
        if (dataEl) {
          let prod = null;
          try { prod = JSON.parse(dataEl.textContent); } catch (e) { prod = null; }
          if (prod && (prod.variants || []).length > 1) {
            openUpsellVariantSheet(prod);
            return;
          }
        }
      }

      const variantId = btn ? btn.getAttribute('data-variant-id') : null;
      if (!variantId || !window.CartAPI) return;
      try {
        await window.CartAPI.addItem(variantId, 1);
      } catch (e) {
        console.error(e);
        return;
      }
      upsellAddedFeedback(btn);
    }

    function bindSheetEvents() {
      // Open/close is handled by ui-sheet.js via data-ui-sheet-trigger/close.
      // Here we only bind the sheet *actions* (note autosave, coupon, shipping).
      const noteArea = drawer.querySelector('[data-cart-note]');
      if (noteArea) {
        noteArea.oninput = function () {
          clearTimeout(notesSaveTimeout);
          notesSaveTimeout = setTimeout(() => saveNote(this.value), 500);
        };
      }
      const couponInput = drawer.querySelector('[data-coupon-input]');
      const couponApply = drawer.querySelector('[data-coupon-apply]');
      if (couponApply) {
        couponApply.onclick = () => applyDiscountCode(couponInput?.value || '');
      }
      if (couponInput) {
        couponInput.oninput = () => clearCouponError();
        couponInput.onkeydown = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyDiscountCode(couponInput.value.trim());
          }
        };
      }
      drawer.querySelectorAll('[data-coupon-apply-code]').forEach((btn) => {
        btn.onclick = () => applyDiscountCode(btn.getAttribute('data-coupon-apply-code'));
      });
      const shipCalc = drawer.querySelector('[data-shipping-calculate]');
      if (shipCalc) {
        shipCalc.onclick = calculateShippingRates;
      }
      drawer.querySelectorAll('[data-cart-upsell-add]').forEach((btn) => {
        btn.onclick = (e) => {
          e.preventDefault();
          if (drawer.classList.contains('is-updating')) return;
          quickAddUpsell(btn);
        };
      });
    }

    function bindEventsOnce() {
      drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          closeDrawer();
          return;
        }
        if (e.target.closest('[data-cart-overlay]')) {
          closeDrawer();
        }
      });
    }

    function formatMoney(cents) {
      if (window.formatMoney) return window.formatMoney(cents);
      if (window.formatMoneyFallback) return window.formatMoneyFallback(cents);
      return (Number(cents || 0) / 100).toFixed(2);
    }

    function getSizedImageUrl(url, size) {
      if (!url) return '';
      return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
    }

    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      // If a ui-sheet (note / shipping / coupon / upsell / quick-view) is open,
      // let ui-sheet.js handle Escape — don't also close the whole drawer.
      if (document.documentElement.classList.contains('ui-sheet-open')) return;
      if (drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        toggleDrawer();
      }
    });

    window.addEventListener('cart:open', openDrawer);
    window.addEventListener('cart:close', closeDrawer);
    window.addEventListener('cart:toggle', toggleDrawer);

    window.addEventListener('cart:changing', () => setDrawerUpdating(true));

    window.addEventListener('cart:updated', () => refreshDrawerFromCart());

    window.addEventListener('cart:error', () => setDrawerUpdating(false));

    window.addEventListener('cart:item-added', () => {
      if (openOnAdd) openDrawer();
    });

    bindEventsOnce();
    bindSheetEvents();
    attachItemListeners();
    clearDrawerShellGsap();
    initGsapLayout();
    if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();

    if (window.gsapReady && typeof gsap === 'undefined') {
      window.gsapReady.then(initGsapLayout);
    }
  }
})();
